import React, { useEffect, useRef, useState } from 'react';
import './index.css';
import { LineSeries, CandlestickSeries, createChart, ColorType, LineStyle, CrosshairMode } from 'lightweight-charts';
import { usePairs } from '../../hooks/usePairs';
import { handleForbiddenResponse } from '../../utils/api';
import { PairSelector } from '../PairSelector';

const UpdateIcon = () => (
  <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 122.61 122.88" width="20" height="20">
    <title>Обновить</title>
    <path d="M111.9,61.57a5.36,5.36,0,0,1,10.71,0A61.3,61.3,0,0,1,17.54,104.48v12.35a5.36,5.36,0,0,1-10.72,0V89.31A5.36,5.36,0,0,1,12.18,84H40a5.36,5.36,0,1,1,0,10.71H23a50.6,50.6,0,0,0,88.87-33.1ZM106.6,5.36a5.36,5.36,0,1,1,10.71,0V33.14A5.36,5.36,0,0,1,112,38.49H84.44a5.36,5.36,0,1,1,0-10.71H99A50.6,50.6,0,0,0,10.71,61.57,5.36,5.36,0,1,1,0,61.57,61.31,61.31,0,0,1,91.07,8,61.83,61.83,0,0,1,106.6,20.27V5.36Z" fill="currentColor" />
  </svg>
);

const FALLBACK_HEIGHT = 320;

const resolveDimensions = (container) => {
  if (!container) {
    return { width: window.innerWidth, height: Math.max(window.innerHeight * 0.6, FALLBACK_HEIGHT) };
  }
  const width = container.clientWidth || window.innerWidth;
  const height = container.clientHeight || Math.max(window.innerHeight * 0.68, FALLBACK_HEIGHT);
  return { width, height };
};

export const TvLiquidationsContainer = (props) => {
  const {
    data,
    colors: {
      backgroundColor = '#0f111d',
      lineColor = '#4a68ff',
      textColor = '#f0f4ff',
      areaTopColor = '#4a68ff',
      areaBottomColor = 'rgba(41, 104, 255, 0.28)',
    } = {},
  } = props;

  const chartContainerRef = useRef(null);

  const [initialData, setData] = useState(null);
  const [estLiquidations, setEstLiquidations] = useState(null);
  const ordersCount = '150';
  const showInitialVol = 'false';
  const amountFrom = null;
  const amountTo = null;

  const [symbol, setSymbol] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const serverUrl = 'https://tkachev-trade.com/api';

  const { data: pairsData, loading: pairsLoading, error: pairsError } = usePairs();
  const pairsInfo = pairsData?.pairsInfo ?? [];
  const selectedPair = pairsInfo.find((pair) => pair.symbol === symbol);
  const activeSymbol = selectedPair?.symbol ?? symbol;

  const [tooltip, setTooltip] = useState(null);
  const [labelPosition, setLabelPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (!pairsInfo.length) {
      return;
    }
    if (!symbol) {
      setSymbol(pairsInfo[0].symbol);
      return;
    }
    const exists = pairsInfo.some((pair) => pair.symbol === symbol);
    if (!exists) {
      setSymbol(pairsInfo[0].symbol);
    }
  }, [pairsInfo, symbol]);

  useEffect(() => {
    if (!activeSymbol) {
      return;
    }
    let isCancelled = false;
    setData(null);

    const token = localStorage.getItem('token');

    fetch(`${serverUrl}/chart/kline?symbol=${activeSymbol}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        if (handleForbiddenResponse(response)) {
          return Promise.reject(new Error('Forbidden'));
        }
        return response.json();
      })
      .then((json) => {
        if (!isCancelled) {
          const items = json?.response?.items ?? [];
          setData(items);
        }
      })
      .catch((error) => {
        if (!isCancelled) {
          console.error('Failed to fetch kline data:', error);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [serverUrl, activeSymbol, reloadKey]);

  useEffect(() => {
    if (!activeSymbol) {
      return;
    }
    let isCancelled = false;
    setEstLiquidations(null);

    const token = localStorage.getItem('token');
    let url = `${serverUrl}/chart/est_liquidations?symbol=${activeSymbol}&ordersCount=${ordersCount}&showInitialVol=${showInitialVol}`;
    if (amountFrom) {
      url += `&amountFrom=${amountFrom}`;
    }
    if (amountTo) {
      url += `&amountTo=${amountTo}`;
    }

    fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        if (handleForbiddenResponse(response)) {
          return Promise.reject(new Error('Forbidden'));
        }
        return response.json();
      })
      .then((json) => {
        if (!isCancelled) {
          const responseData = json?.response ?? null;
          setEstLiquidations(responseData);
        }
      })
      .catch((error) => {
        if (!isCancelled) {
          console.error('Failed to fetch estimated liquidations data:', error);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [
    serverUrl,
    activeSymbol,
    reloadKey,
    ordersCount,
    showInitialVol,
    amountFrom,
    amountTo,
  ]);

  useEffect(() => {
    const container = chartContainerRef.current;
    if (!container) {
      return undefined;
    }

    const { width, height } = resolveDimensions(container);

    const chart = createChart(container, {
      layout: {
        background: { type: ColorType.Solid, color: backgroundColor },
        textColor,
      },
      grid: {
        vertLines: {
          color: '#6d6a6aff',   // vertical grid lines
          style: 1,           // solid (0 = none, 1 = solid, 2 = dotted, etc.)
        },
        horzLines: {
          color: '#6d6a6aff',   // horizontal grid lines
          style: 1,
        },
      },
      width,
      height,
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
        fixRightEdge: true,
      },
      handleScroll: {},
      rightPriceScale: {
        autoScale: false,
        ticksVisible: true,
      },
      crosshair: {
        mode: CrosshairMode.Normal,  // free crosshair
      },
    });

    const resize = () => {
      const nextDimensions = resolveDimensions(container);
      chart.applyOptions({
        width: nextDimensions.width,
        height: nextDimensions.height,
      });
    };

    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null;
    window.addEventListener('resize', resize);
    if (resizeObserver) {
      resizeObserver.observe(container);
    }

    chart.timeScale().fitContent();

    const priceFmt = {
      type: 'price',
      precision: selectedPair?.precision ?? 4,
      minMove: selectedPair?.step ?? 0.0001,
    };

    const ordersToPlot = estLiquidations?.orders ?? [];

    const lines = ordersToPlot.map((element) => {
      const estLiquiLine = chart.addSeries(LineSeries, {
        color: element.color,
        lineStyle: LineStyle.Solid,
        lineWidth: 2,
        priceLineVisible: false,
        lastValueVisible: false,
        priceFormat: priceFmt,
      });
      estLiquiLine.setData([
        { value: element.price, time: element.start },
        { value: element.price, time: element.end },
      ]);

      return { estLiquiLine, element };
    });

    const handleCrosshairMove = (param) => {
      if (!param?.point) return;

      const crosshairX = param.point.x;
      const crosshairY = param.point.y;

      let bestHit = null;
      let bestDist = Infinity;
      const tolerance = 3;

      lines.forEach(({ estLiquiLine, element }) => {
        const x1 = chart.timeScale().timeToCoordinate(element.start);
        const y1 = estLiquiLine.priceToCoordinate(element.price);
        const x2 = chart.timeScale().timeToCoordinate(element.end);
        const y2 = estLiquiLine.priceToCoordinate(element.price);

        if ([x1, y1, x2, y2].some(v => typeof v !== "number")) return;

        const dist = distancePointToSegment(crosshairX, crosshairY, x1, y1, x2, y2);

        if (dist <= tolerance && dist < bestDist) {
          bestDist = dist;
          bestHit = {
            content: formatUSDShort(element.amountUsd),
            x: crosshairX,
            y: crosshairY,
          };
        }
      });

      if (bestHit) {
        setTooltip({ content: bestHit.content });
        setLabelPosition({ x: bestHit.x, y: bestHit.y });
      } else {
        setTooltip(null);
      }
    };

    chart.subscribeCrosshairMove(handleCrosshairMove);

    function formatUSDShort(amount) {
      if (amount === null || amount === undefined || isNaN(amount)) return "";

      const absAmount = Math.abs(amount);
      let formatted;

      if (absAmount >= 1e9) {
        formatted = (amount / 1e9).toFixed(1) + " Млрд";
      } else if (absAmount >= 1e6) {
        formatted = (amount / 1e6).toFixed(1) + " Млн";
      } else if (absAmount >= 1e3) {
        formatted = (amount / 1e3).toFixed(1) + " Тыс";
      } else {
        formatted = amount.toFixed(2);
      }
      return formatted.replace(".", ",");
    }


    function distancePointToSegment(px, py, x1, y1, x2, y2) {
      const dx = x2 - x1;
      const dy = y2 - y1;

      if (dx === 0 && dy === 0) {
        // segment is just a point
        return Math.hypot(px - x1, py - y1);
      }

      // Project point onto line, clamped [0,1] for segment
      const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / (dx * dx + dy * dy)));
      const projX = x1 + t * dx;
      const projY = y1 + t * dy;

      return Math.hypot(px - projX, py - projY);
    }

    const newSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#26a69a',
      downColor: '#ef5350',
      borderVisible: false,
      wickUpColor: '#26a69a',
      wickDownColor: '#ef5350',
      priceScaleId: 'right',
      priceFormat: priceFmt,
    });

    if (initialData) {
      newSeries.setData(initialData);
    }

    return () => {
      window.removeEventListener('resize', resize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      chart.remove();
    };
  }, [
    data,
    backgroundColor,
    lineColor,
    textColor,
    areaTopColor,
    areaBottomColor,
    initialData,
    estLiquidations,
    selectedPair?.precision,
    selectedPair?.step,
  ]);

  const handlePairChange = (nextSymbol) => {
    setSymbol(nextSymbol);
  };

  const handleRefresh = () => {
    if (!activeSymbol) {
      return;
    }
    setReloadKey((prev) => prev + 1);
  };

  const Tooltip = ({ content, x, y }) => {
    return (
      <div
        style={{
          position: 'absolute',
          top: `${y - 30}px`,  // Adjust the Y position
          left: `${x}px`, // Adjust the X position
          background: 'rgba(0, 0, 0, 0.7)',
          color: 'white',
          padding: '5px',
          borderRadius: '5px',
          fontSize: '12px',
          pointerEvents: 'none', // So it doesn't block interaction with the chart
          zIndex: 9999, // Ensure the tooltip is above other elements
        }}
      >
        {content}
      </div>
    );
  };

  return (
    <div className="chart-panel">
      <div className="chart-panel__search">
        <PairSelector
          pairsInfo={pairsInfo}
          value={symbol}
          onChange={handlePairChange}
          loading={pairsLoading}
          error={pairsError?.message}
        />
      </div>
      <div className="chart-panel__header">
        <div className="chart-panel__selector">
          <select
            className="chart-panel__dropdown"
            value={symbol}
            onChange={(e) => handlePairChange(e.target.value)}
            disabled={pairsLoading}
          >
            {pairsInfo.map((pair) => (
              <option key={pair.symbol} value={pair.symbol}>
                {pair.symbol}
              </option>
            ))}
          </select>
        </div>
        <div className="chart-panel__actions">
          <button
            type="button"
            className="chart-panel__update-button"
            onClick={handleRefresh}
            title={`Обновить ${activeSymbol || ''}`}
          >
            <UpdateIcon />
          </button>
        </div>
      </div>
      <div className="chart-panel__body">
        <div
          ref={chartContainerRef}
          className="chart-panel__canvas"
        />
        {tooltip && (<Tooltip content={tooltip.content} x={labelPosition.x} y={labelPosition.y} />
        )}
      </div>
    </div>
  );
};
